import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, Building2, Calendar as CalendarIcon, Check, CheckCircle, Edit,
  Gift, Info, Loader2, MapPin, Search, Send, Shield, Sparkles, Star,
  TrendingDown, X, XCircle,
} from 'lucide-react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

import { CountrySelector, getCustomerCountry } from '../components/CountrySelector';
import { DateRangeSheet } from '../components/DateRangeSheet';
import { FadeInSection } from '../components/FadeInSection';
import { FeaturedHotelsCarousel } from '../components/FeaturedHotelsCarousel';
import { PopularDestinationsCarousel, PopularDestinationsGrid } from '../components/PopularDestinations';
import VideoBackground, { GradientBackground } from '../components/VideoBackground';

// Re-imports of components already extracted in earlier phases
import { AuthDialog, AuthDialogContent } from '../components/AuthDialog';

// Circular imports from App.js (named, used only inside render bodies — safe).
// - useAuth: hook, exported in Fase 3b
// - GuestSelector + DestinationAutocomplete: components, exported in Fase 4b-1
// - LastMinuteSection: uses Sunhotels b2c=1 API (separate flow, kept inline in App.js
//   to avoid coupling with main b2c=0 search). Marked for future extraction decision.
import {
  useAuth,
  GuestSelector,
  DestinationAutocomplete,
  LastMinuteSection,
} from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image proxy helper — re-defined locally (was in App.js).
// Routes external hotel images (sunhotels, cupid, hotelbeds, etc.) through
// our /api/proxy/image endpoint which resizes + converts to WebP + caches.
const proxyImage = (url, width = 800, quality = 80) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('/api/proxy/image') || url.startsWith('/') || url.includes('/api/media/')) return url;
  const allowlist = ['hotelimages.sunhotels.net', 'static.cupid.travel', 'media.cupid.travel', 'exp.cdn-hotels.com', 'images.trvl-media.com', 'photos.hotelbeds.com'];
  try {
    const u = new URL(url);
    if (!allowlist.some(h => u.hostname.endsWith(h))) return url;
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
  } catch {
    return url;
  }
};

/**
 * HomePage — Public landing page with hero search, popular destinations,
 * featured hotels, last-minute deals (b2c=1 API), testimonials, and FAQ.
 *
 * Includes two HomePage-only helpers inline below:
 *   - TestimonialSubmitForm: lets visitors submit a testimonial for moderation
 *   - TestimonialsSection: carousel of approved testimonials
 *
 * Extracted from App.js (Fase 4b) — pure relocation, no logic changes.
 */

// ==================== TESTIMONIAL SUBMIT FORM (HomePage helper) ====================
const TestimonialSubmitForm = ({ isOpen, onClose, bookingId = '', hotelName = '' }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('freestays_token');
      if (!token) {
        toast.error('Please log in to submit a review');
        return;
      }
      
      await axios.post(`${API}/testimonials`, {
        booking_id: bookingId,
        hotel_name: hotelName,
        rating,
        title,
        content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your review has been submitted for approval.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              {submitted ? 'Thank You!' : 'Share Your Experience'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Review Submitted!</h3>
              <p className="text-muted-foreground mb-4">
                Your review has been submitted and will be visible after approval.
              </p>
              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {hotelName && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Reviewing:</p>
                  <p className="font-medium">{hotelName}</p>
                </div>
              )}
              
              {/* Rating */}
              <div className="space-y-2">
                <Label>Your Rating</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="review-title">Review Title</Label>
                <Input
                  id="review-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Summarize your experience..."
                  maxLength={100}
                />
              </div>
              
              {/* Content */}
              <div className="space-y-2">
                <Label htmlFor="review-content">Your Review</Label>
                <Textarea
                  id="review-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Tell us about your stay..."
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground text-right">{content.length}/500</p>
              </div>
              
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Review
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== TESTIMONIALS SECTION (HomePage helper) ====================
const TestimonialsSection = ({ bgOverride = null }) => {
  const { t } = useTranslation();
  const { user } = useAuth();  // Get user from auth context
  const { data, isLoading } = useQuery({
    queryKey: ['testimonials'],
    queryFn: async () => {
      const response = await axios.get(`${API}/testimonials`);
      return response.data.testimonials;
    }
  });

  const testimonials = data || [];

  if (isLoading) {
    return (
      <section className={`py-20 md:py-28 ${bgOverride ? '' : 'bg-gray-50 dark:bg-[#0a2540]'}`} style={bgOverride ? { backgroundColor: bgOverride } : undefined}>
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 text-center">
          <div className="mb-16">
            <Badge className="mb-4 bg-[#E8A317]/10 text-[#081D33] dark:text-[#E8A317] border-[#E8A317]/30">
              <Star className="w-3 h-3 mr-1 fill-current" /> {t('testimonials.badge')}
            </Badge>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-4" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
              <span className="block text-white">{t('testimonials.smartTitle1', 'Loved by Travelers')}</span>
              <span className="block" style={{color: '#E8A317'}}>{t('testimonials.smartTitle2', 'Hear Their Stories')}</span>
            </h2>
          </div>
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section 
      className={`py-20 md:py-28 overflow-hidden ${bgOverride ? '' : 'bg-gray-50 dark:bg-[#0a2540]'}`}
      style={bgOverride ? { backgroundColor: bgOverride } : undefined}
      data-testid="testimonials-section"
    >
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-[#E8A317]/10 text-[#081D33] dark:text-[#E8A317] border-[#E8A317]/30">
            <Star className="w-3 h-3 mr-1 fill-current" /> {t('testimonials.badge')}
          </Badge>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-4" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
            <span className="block text-white">{t('testimonials.smartTitle1', 'Loved by Travelers')}</span>
            <span className="block" style={{color: '#E8A317'}}>{t('testimonials.smartTitle2', 'Hear Their Stories')}</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.slice(0, 4).map((testimonial, index) => (
            <Card 
              key={testimonial.testimonial_id} 
              className="p-6 rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card relative overflow-hidden group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Decorative gradient */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#00A9E0]/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 group-hover:from-[#00A9E0]/10 transition-all" />
              
              {/* Rating Stars */}
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`} 
                  />
                ))}
              </div>
              
              {/* Title */}
              <h4 className="font-semibold text-base mb-2 line-clamp-1">{testimonial.title}</h4>
              
              {/* Content */}
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3 leading-relaxed">
                "{testimonial.content}"
              </p>
              
              {/* Author & Hotel */}
              <div className="pt-4 border-t border-border/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00A9E0] to-[#0077B6] flex items-center justify-center text-white font-semibold text-sm">
                    {testimonial.user_name?.charAt(0) || 'G'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{testimonial.user_name}</p>
                    {testimonial.hotel_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {testimonial.hotel_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <div className="text-center p-6 rounded-2xl bg-card shadow-md">
            <div className="text-3xl md:text-4xl font-bold text-primary mb-2">4.9</div>
            <div className="flex justify-center gap-0.5 mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{t('testimonials.averageRating')}</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-card shadow-md">
            <div className="text-3xl md:text-4xl font-bold text-primary mb-2">15K+</div>
            <p className="text-sm text-muted-foreground mt-2">{t('testimonials.happyGuests')}</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-card shadow-md">
            <div className="text-3xl md:text-4xl font-bold text-primary mb-2">€2.5M</div>
            <p className="text-sm text-muted-foreground mt-2">{t('testimonials.totalSaved')}</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-card shadow-md">
            <div className="text-3xl md:text-4xl font-bold text-primary mb-2">98%</div>
            <p className="text-sm text-muted-foreground mt-2">{t('testimonials.wouldRecommend')}</p>
          </div>
        </div>
        
        {/* Write Review CTA */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">{t('testimonials.shareExperience')}</p>
          <Button
            variant="outline"
            size="lg"
            onClick={async () => {
              // Check if user is logged in using auth context
              if (!user) {
                toast.error(t('testimonials.loginRequired'));
                return;
              }
              
              const token = localStorage.getItem('freestays_token');
              if (!token) {
                toast.error(t('testimonials.loginRequired'));
                return;
              }
              
              // Check if user is eligible to write a review
              try {
                const response = await axios.get(`${API}/testimonials/can-review`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (!response.data.can_review) {
                  // Show specific message based on reason
                  if (response.data.reason === 'login_required') {
                    toast.error(t('testimonials.loginRequired'));
                  } else {
                    toast.error(response.data.message || t('testimonials.needStay'));
                  }
                  return;
                }
                
                // User is eligible - open the review form with their completed bookings
                window.dispatchEvent(new CustomEvent('openReviewForm', { 
                  detail: { completedBookings: response.data.completed_bookings }
                }));
              } catch (error) {
                console.error('Review eligibility check failed:', error);
                // Check if it's an auth error
                if (error.response?.status === 401) {
                  toast.error(t('testimonials.loginRequired'));
                } else {
                  toast.error(error.response?.data?.detail || error.response?.data?.message || 'Unable to verify review eligibility. Please try again.');
                }
              }
            }}
            className="gap-2"
          >
            <Edit className="w-4 h-4" />
            {t('testimonials.writeReview')}
          </Button>
        </div>
      </div>
    </section>
  );
};

// ==================== HOMEPAGE ====================
export const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasValidPass } = useAuth();
  const [destination, setDestination] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [resortId, setResortId] = useState("");
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  // legacy hero popover state (kept for backward compat with other refs)
  const [heroCheckinOpen, setHeroCheckinOpen] = useState(false);
  const [heroCheckoutOpen, setHeroCheckoutOpen] = useState(false);
  
  // Parallax offset for hero background (0.4x scroll speed)
  const [heroParallax, setHeroParallax] = useState(0);
  useEffect(() => {
    // Disable parallax on mobile for performance + better UX
    if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) return;
    // Respect user's motion preference
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setHeroParallax(Math.min(window.scrollY * 0.4, 200));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);
  const [guests, setGuests] = useState({ adults: 2, children: 0, rooms: 1 });
  const [childrenAges, setChildrenAges] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null); // For direct hotel selection
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  
  // Homepage background settings from admin
  const [homepageBg, setHomepageBg] = useState({
    video_url: '/videos/beach-waves.webm',
    video_url_mp4: '/videos/beach-waves.mp4',
    poster_url: '/videos/beach-poster.jpg',
    text_color: 'white',
    overlay_opacity: 30,
    enabled: true,
    background_type: 'video',  // 'video' | 'gradient' | 'image' | 'color'
    gradient_style: 'ocean',   // preset gradient style
    solid_color: '#081D33'     // solid background color
  });
  const [heroBg, setHeroBg] = useState({
    video_url: '/videos/beach-waves.webm',
    video_url_mp4: '/videos/beach-waves.mp4',
    poster_url: '/videos/beach-poster.jpg',
    text_color: 'white',
    overlay_opacity: 30,
    enabled: false,  // Default disabled - shows Unsplash photo
    background_type: 'video',
    gradient_style: 'ocean'
  });
  
  // Site Content from database (editable via admin)
  // Initialize as empty so getContent falls back to locale translations via t()
  // CMS content will be loaded from API if available
  const [siteContent, setSiteContent] = useState({});
  
  // Price comparison data from admin (manual comparisons) — count configurable in admin
  const { data: homepageComparisonData } = useQuery({
    queryKey: ["homepage-comparison-data"],
    queryFn: async () => {
      const res = await axios.get(`${BACKEND_URL}/api/public/comparison-data?context=manual`);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
  const homepageComparisons = (homepageComparisonData?.comparisons || []).filter(
    c => c.hotels && c.hotels.length > 0 && c.hotels[0].freestays_price > 0
  );
  
  // Combined homepage initialization - single API call replaces multiple calls
  const [homepageInitLoaded, setHomepageInitLoaded] = useState(false);
  
  useEffect(() => {
    const loadHomepageInit = async () => {
      try {
        const lang = (i18n.language || 'en').split('-')[0];
        const res = await axios.get(`${API}/homepage-init?lang=${lang}`);
        const data = res.data;
        
        // Set homepage background (for lower section - Last Minute, Passes, etc.)
        if (data.homepage_background?.settings) {
          setHomepageBg(data.homepage_background.settings);
        }
        
        // Set hero background (for top section - Search)
        if (data.hero_background?.settings) {
          setHeroBg(data.hero_background.settings);
        }
        
        // Set site content - handle both direct and nested content structure
        if (data.site_content && Object.keys(data.site_content).length > 0) {
          const contentData = data.site_content.content || data.site_content;
          // Replace site content with API data for current language
          setSiteContent(contentData);
        } else {
          // No CMS content for this language — clear state so getContent
          // falls through to locale file translations via t()
          setSiteContent({});
        }
        
        // Set footer settings
        if (data.footer_settings && Object.keys(data.footer_settings).length > 0) {
          setFooterSettings(data.footer_settings);
        }
        
        // Set destinations
        if (data.destinations?.destinations?.length > 0) {
          setFeaturedDestinations(data.destinations.destinations);
          setDestinationsCount(data.destinations.display_count || 4);
        }
        
        setHomepageInitLoaded(true);
      } catch (err) {
        console.log('Using default homepage settings');
        // Clear CMS content on error so getContent uses locale translations
        setSiteContent({});
        setHomepageInitLoaded(true);
      }
    };
    loadHomepageInit();
  }, [i18n.language]);  // Reload when language changes
  
  // Helper function to get content with database override
  // If database has content, use it; otherwise fall back to translation
  const getContent = (section, key, fallbackTranslationKey) => {
    // Use CMS content for all languages; fall back to locale translations if CMS has no value
    if (siteContent?.[section]?.[key]) {
      return siteContent[section][key];
    }
    return t(fallbackTranslationKey);
  };
  
  // Country-aware destination for "View all" link
  const [userCountryDestination, setUserCountryDestination] = useState(null);
  
  // Detect user's country and get corresponding destination ID
  useEffect(() => {
    const detectCountryDestination = async () => {
      try {
        // Get user's country from backend geolocation endpoint (proxy to avoid CORS)
        const geoRes = await axios.get(`${API}/geolocation`);
        const countryName = geoRes.data?.country;
        const countryCode = geoRes.data?.countryCode;
        
        if (countryName) {
          // Map common country names to Sunhotels search terms
          const countryMapping = {
            'United States': 'USA',
            'United Kingdom': 'England', // Sunhotels uses England, Scotland, Wales separately
            'Great Britain': 'England',
            'The Netherlands': 'Netherlands',
            'Czech Republic': 'Czechia',
            'Czechia': 'Czech Republic',
          };
          
          const searchTerm = countryMapping[countryName] || countryName;
          
          // Search for the country destination to get its ID
          const destRes = await axios.get(`${API}/destinations/search?q=${encodeURIComponent(searchTerm)}`);
          const destinations = destRes.data?.destinations || [];
          
          // Find a destination that matches the user's country (type=country)
          const countryDest = destinations.find(d => 
            d.type === 'country' && 
            (d.name?.toLowerCase() === searchTerm?.toLowerCase() ||
             d.name?.toLowerCase() === countryName?.toLowerCase())
          );
          
          if (countryDest) {
            setUserCountryDestination({
              destinationId: countryDest.id || countryDest.destination_id,
              name: countryDest.name
            });
          }
        }
      } catch (err) {
        console.log('Could not detect country destination:', err);
      }
    };
    
    detectCountryDestination();
  }, []);
  
  // Listen for review form open event
  useEffect(() => {
    const handleOpenReview = () => setShowReviewForm(true);
    window.addEventListener('openReviewForm', handleOpenReview);
    return () => window.removeEventListener('openReviewForm', handleOpenReview);
  }, []);

  // Handle URL params for login trigger (from pass expiration email links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldLogin = params.get('login') === 'true';
    const renewType = params.get('renew');
    const upgradeType = params.get('upgrade');
    
    if (shouldLogin && !user) {
      setShowLoginDialog(true);
      // Clean up URL params after handling
      const newParams = new URLSearchParams(location.search);
      newParams.delete('login');
      const newUrl = newParams.toString() ? `${location.pathname}?${newParams.toString()}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    // If user is already logged in and wants to renew/upgrade, redirect to dashboard
    if (user && (renewType || upgradeType)) {
      navigate('/dashboard');
    }
  }, [location, user, navigate]);

  const handleSearch = () => {
    // Fallback to next-day + 3 nights if user starts a search without picking dates
    const effCheckIn = checkIn || addDays(new Date(), 1);
    const effCheckOut = checkOut || addDays(effCheckIn, 3);

    // If a specific hotel is selected, go directly to hotel page
    if (selectedHotel) {
      const checkInStr = format(effCheckIn, 'yyyy-MM-dd');
      const checkOutStr = format(effCheckOut, 'yyyy-MM-dd');
      const destParam = selectedHotel.city ? `&destination=${encodeURIComponent(selectedHotel.city)}` : '';
      const destIdParam = selectedHotel.dest_id ? `&destinationId=${selectedHotel.dest_id}` : '';
      const resortIdParam = selectedHotel.resort_id ? `&resortId=${selectedHotel.resort_id}` : '';
      navigate(`/hotel/${selectedHotel.hotel_id}?checkIn=${checkInStr}&checkOut=${checkOutStr}&adults=${guests.adults}&children=${guests.children}${destParam}${destIdParam}${resortIdParam}`);
      return;
    }

    const params = new URLSearchParams({
      destination,
      destinationId: destinationId || '',
      resortId: resortId || '',
      checkIn: format(effCheckIn, 'yyyy-MM-dd'),
      checkOut: format(effCheckOut, 'yyyy-MM-dd'),
      adults: guests.adults.toString(),
      children: guests.children.toString(),
      childrenAges: childrenAges.join(','),
      rooms: guests.rooms.toString(),
      customerCountry: getCustomerCountry()
    });
    // Add nearby params if user searched via geolocation
    const nearbyCoords = sessionStorage.getItem('nearbyCoords');
    if (nearbyCoords) {
      try {
        const { lat, lng } = JSON.parse(nearbyCoords);
        params.set('nearby', 'true');
        params.set('userLat', lat.toString());
        params.set('userLng', lng.toString());
      } catch (e) {}
      sessionStorage.removeItem('nearbyCoords');
    }
    navigate(`/search?${params.toString()}`);
  };

  const handleDestinationSelect = (dest) => {
    const displayParts = [dest.name];
    if (dest.city) displayParts.push(dest.city);
    if (dest.country) displayParts.push(dest.country);
    const displayName = displayParts.join(', ');
    
    // If it's a hotel, store hotel info and let user pick dates
    if (dest.type === 'hotel' && dest.hotel_id) {
      setSelectedHotel({
        hotel_id: dest.hotel_id,
        name: dest.name,
        dest_id: dest.id,
        resort_id: dest.resort_id || '',
        thumbnail: dest.thumbnail
      });
      setDestination(displayName);
      setDestinationId(dest.id);
      return;
    }
    // Otherwise, set destination for search
    setSelectedHotel(null); // Clear any selected hotel
    setDestination(displayName);
    setDestinationId(dest.id);
    setResortId(dest.resort_id || '');
  };

  const clearSelectedHotel = () => {
    setSelectedHotel(null);
    setDestination('');
    setDestinationId('');
  };

  const [featuredDestinations, setFeaturedDestinations] = useState([
    { name: "Santorini", country: "Greece", image: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=600", hotels: "1,240+", destination_id: "16330" },
    { name: "Barcelona", country: "Spain", image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=600", hotels: "2,100+", destination_id: "17429" },
    { name: "Vienna", country: "Austria", image: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=600", hotels: "890+", destination_id: "18180" },
    { name: "Amalfi", country: "Italy", image: "https://images.unsplash.com/photo-1612698093158-e07ac200d44e?w=600", hotels: "450+", destination_id: "10515" }
  ]);
  const [destinationsCount, setDestinationsCount] = useState(4);
  
  // Footer settings state
  const [footerSettings, setFooterSettings] = useState(null);

  // NOTE: Destinations and footer settings are now loaded via homepage-init endpoint
  // See the combined useEffect above that calls /api/homepage-init

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* NOTE: AnimatedBackground with flight paths/particles removed - was blocking video background */}
      
      {/* Login Dialog triggered from URL params (pass expiration emails) */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <AuthDialogContent onClose={() => setShowLoginDialog(false)} />
        </DialogContent>
      </Dialog>
      
      {/* Hero Section - Premium Design with Separate Hero Background Settings */}
      {/* Backgrounds start BELOW the fixed top header (80px) so they don't bleed under the nav */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden z-10" style={{ paddingTop: '80px' }}>
        {/* Video Background - Only when heroBg enabled and type is video */}
        {heroBg.enabled && heroBg.background_type === 'video' && heroBg.video_url && (
          <div className="absolute inset-x-0 bottom-0" style={{ top: '80px', transform: `translate3d(0, ${heroParallax * 0.5}px, 0)`, willChange: 'transform' }}>
            <video 
              autoPlay 
              muted 
              loop 
              playsInline
              poster={heroBg.poster_url}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: 0.7 }}
            >
              <source src={heroBg.video_url} type="video/webm" />
              <source src={heroBg.video_url_mp4} type="video/mp4" />
            </video>
          </div>
        )}
        {/* Gradient Background - Only when heroBg enabled and type is gradient */}
        {heroBg.enabled && heroBg.background_type === 'gradient' && (
          <div 
            className={`absolute inset-x-0 bottom-0 bg-gradient-to-br ${
              heroBg.gradient_style === 'ocean' ? 'from-blue-600 via-cyan-500 to-teal-400' :
              heroBg.gradient_style === 'sunset' ? 'from-orange-500 via-pink-500 to-purple-600' :
              heroBg.gradient_style === 'forest' ? 'from-green-600 via-emerald-500 to-teal-400' :
              heroBg.gradient_style === 'royal' ? 'from-indigo-600 via-purple-500 to-pink-400' :
              heroBg.gradient_style === 'midnight' ? 'from-slate-900 via-purple-900 to-slate-800' :
              heroBg.gradient_style === 'warmth' ? 'from-amber-500 via-orange-400 to-yellow-300' :
              heroBg.gradient_style === 'arctic' ? 'from-cyan-400 via-blue-300 to-indigo-400' :
              heroBg.gradient_style === 'neutral' ? 'from-white via-gray-100 to-gray-300' :
              'from-blue-600 via-cyan-500 to-teal-400'
            }`}
            style={{ top: '80px' }}
          />
        )}
        {/* Default: Static Background Image (when heroBg disabled or type is image) */}
        {(!heroBg.enabled || heroBg.background_type === 'image') && (() => {
          const fit = heroBg.image_fit || 'cover';            // cover | contain | fill
          const position = heroBg.image_position || 'center'; // top | center | bottom | left | right ...
          const zoom = Math.max(100, Math.min(150, Number(heroBg.image_zoom || 100))) / 100;
          const parallaxOn = heroBg.parallax_enabled !== false; // default true
          const imageUrl = heroBg.enabled && heroBg.image_url
            ? (heroBg.image_url.startsWith('http') ? heroBg.image_url : `${BACKEND_URL}${heroBg.image_url}`)
            : '/api/media/image/banner_pool.webp';
          // Map fit -> background-size. `contain` shows the WHOLE image, `cover`
          // crops to fit, `fill` (=100% 100%) stretches.
          const bgSize = fit === 'contain' ? 'contain' : (fit === 'fill' ? '100% 100%' : 'cover');
          // Adjust dynamic transform: only apply zoom + parallax translate when
          // parallax is enabled. Otherwise just leave the image as-is.
          const transform = parallaxOn
            ? `scale(${zoom}) translate3d(0, ${heroParallax * 0.25}px, 0)`
            : (zoom === 1 ? 'none' : `scale(${zoom})`);

          // Mobile / tablet (<1024px) always use cover + center + no parallax so
          // the image is never stretched ("samengetrokken") or shrunk away
          // ("foto valt weg") regardless of the admin's desktop preferences.
          // Desktop (≥1024px) respects the admin-chosen fit / position / parallax.
          return (
            <>
              {/* Mobile / Tablet — viewport-safe rendering */}
              <div
                className="absolute inset-x-0 bottom-0 lg:hidden"
                style={{
                  top: '80px',
                  backgroundImage: `url('${imageUrl}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#081D33',
                  opacity: 0.85,
                }}
                data-testid="hero-bg-mobile"
              />
              {/* Desktop — admin-controlled rendering */}
              <div
                className="absolute inset-x-0 bottom-0 hidden lg:block"
                style={{
                  top: '80px',
                  backgroundImage: `url('${imageUrl}')`,
                  backgroundSize: bgSize,
                  backgroundPosition: position,
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#081D33',
                  transform,
                  transformOrigin: 'center center',
                  willChange: parallaxOn ? 'transform' : 'auto',
                  opacity: 0.85,
                }}
                data-testid="hero-bg-desktop"
              />
            </>
          );
        })()}
        {/* Premium gradient overlay - Deep navy (also offset so overlays only cover the image area) */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-b from-[#081D33]/60 via-[#081D33]/30 to-[#081D33]/80" style={{ top: '80px' }} />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-r from-[#081D33]/50 via-transparent to-transparent" style={{ top: '80px' }} />
        
        {/* Animated accent elements */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#00A9E0]/10 rounded-full blur-[100px] animate-pulse hidden lg:block" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#E8A317]/8 rounded-full blur-[80px] animate-pulse hidden lg:block" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 max-w-[1760px] mx-auto px-4 md:px-8 w-full pt-28 md:pt-32 pb-20 md:pb-24" style={{paddingTop: 'clamp(6rem, 10vw, 9rem)', paddingBottom: 'clamp(4rem, 8vw, 7rem)'}}>
          <div className="grid lg:grid-cols-2 items-center min-w-0" style={{gap: 'clamp(2rem, 4vw, 4.5rem)'}}>
            {/* Left: Hero Content - Shows SECOND on mobile, FIRST on desktop */}
            <div className="text-center lg:text-left order-2 lg:order-1 min-w-0" style={{display:'flex', flexDirection:'column', gap: 'clamp(1rem, 2vw, 2rem)'}}>
              {/* Logo Video - Style C: Border frame */}
              <div className="flex justify-center lg:justify-start">
                <div className="border-4 border-white/50 rounded-lg p-1 shadow-lg">
                  <video 
                    src="/assets/logo.webm"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="w-auto object-contain rounded"
                    style={{height: 'clamp(2.5rem, 5vw, 5rem)'}}
                    data-testid="hero-logo-video"
                  />
                </div>
              </div>
              
              {/* Premium Badge */}
              <div
                className="self-center lg:self-start inline-flex items-center gap-2 bg-[#081D33]/80 backdrop-blur-md text-white rounded-full font-semibold border border-white/20 shadow-xl"
                style={{
                  padding: 'clamp(0.5rem, 0.7vw + 0.3rem, 0.75rem) clamp(0.9rem, 1.5vw, 1.5rem)',
                  fontSize: 'clamp(0.75rem, 0.9vw + 0.4rem, 0.95rem)',
                }}
                data-testid="hero-badge"
              >
                <span className="w-2 h-2 bg-[#E8A317] rounded-full animate-pulse" />
                {getContent('hero', 'badge', 'hero.badge')}
              </div>
              
              {/* Main Headline with text shadow — fluid scaling */}
              <h1
                className="text-white font-extrabold leading-[1.05] tracking-tight"
                style={{
                  fontSize: 'clamp(2.25rem, 5.5vw + 0.5rem, 4.5rem)',
                  textShadow: '2px 3px 6px rgba(0,0,0,0.5)',
                }}
              >
                <span className="block">{getContent('hero', 'title1', 'hero.title1')}</span>
                <span className="block text-[#E8A317]">{getContent('hero', 'title2', 'hero.title2')}</span>
              </h1>
              
              {/* Subtitle — fluid */}
              <p
                className="text-white/90 max-w-lg mx-auto lg:mx-0 leading-relaxed"
                style={{
                  fontSize: 'clamp(1rem, 0.8vw + 0.75rem, 1.35rem)',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.4)',
                  whiteSpace: 'pre-line',
                }}
                data-testid="hero-subtitle"
              >
                {getContent('hero', 'subtitle', 'hero.subtitle')}
              </p>

              {/* Value highlights - Banner-style benefit tags — fluid */}
              <div
                className="flex flex-wrap justify-center lg:justify-start"
                style={{gap: 'clamp(0.5rem, 1vw, 1rem)'}}
              >
                {[
                  { icon: <Check className="text-[#E8A317]" style={{width:'clamp(0.85rem,1vw,1.1rem)', height:'clamp(0.85rem,1vw,1.1rem)'}} />, text: getContent('benefits', 'item1_title', 'hero.pillar1Title') },
                  { icon: <Check className="text-[#00A9E0]" style={{width:'clamp(0.85rem,1vw,1.1rem)', height:'clamp(0.85rem,1vw,1.1rem)'}} />, text: getContent('benefits', 'item2_title', 'hero.pillar2Title') },
                  { icon: <Check className="text-[#FFAF00]" style={{width:'clamp(0.85rem,1vw,1.1rem)', height:'clamp(0.85rem,1vw,1.1rem)'}} />, text: getContent('benefits', 'item3_title', 'hero.pillar3Title') },
                ].map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-[#081D33]/80 backdrop-blur-md rounded-full border border-white/20 shadow-sm"
                    style={{padding: 'clamp(0.4rem, 0.6vw + 0.2rem, 0.6rem) clamp(0.75rem, 1.2vw, 1.15rem)'}}
                  >
                    {b.icon}
                    <span className="text-white font-semibold" style={{fontSize:'clamp(0.75rem, 0.7vw + 0.5rem, 0.95rem)'}}>
                      {b.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start pt-2 w-full">
                <Button 
                  size="lg" 
                  onClick={() => document.querySelector('[data-testid="search-btn"]')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto rounded-xl bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white font-bold shadow-[0_4px_0_#CC6200,0_5px_10px_rgba(0,0,0,0.3)] hover:brightness-110 active:shadow-[0_0px_0_#CC6200] active:translate-y-[4px] border-0 h-12 sm:h-14 px-5 sm:px-8 text-sm sm:text-base transition-all"
                  data-testid="hero-cta-search"
                >
                  <Search className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                  {t('hero.secondaryCta', 'Search Hotels Now')}
                </Button>
                <Link to="/pass" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-xl h-12 sm:h-14 px-5 sm:px-8 text-sm sm:text-base bg-white/10 border-2 border-white/40 text-white hover:bg-white/20 backdrop-blur-sm font-semibold" data-testid="hero-cta-pass">
                    {t('passPage.title', 'Freestays Pass')}
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Tertiary link */}
              <div className="flex justify-center lg:justify-start">
                <Link to="/comparison" className="text-white/60 hover:text-white text-sm underline underline-offset-4 transition-colors" data-testid="hero-link-comparison">
                  {t('comparisonPage.title', 'The Freestays Advantage')} →
                </Link>
              </div>
              
              {/* Trust indicators - simplified */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-white/60 pt-4">
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4" /> {t('hero.trust1')}
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" /> {t('hero.trust2')}
                </span>
              </div>
            </div>

            {/* Right: Search Card - Shows FIRST on mobile, SECOND on desktop */}
            <Card
              className="rounded-3xl shadow-2xl border-0 bg-white dark:bg-[#0a2540] order-1 lg:order-2 min-w-0"
              style={{padding: 'clamp(1.25rem, 2vw + 0.5rem, 2.25rem)'}}
              data-testid="search-card"
            >
              <div className="text-center mb-6">
                <h3 className="font-bold mb-2" style={{fontSize:'clamp(1.25rem, 1.2vw + 0.75rem, 1.75rem)'}}>{t('search.title')}</h3>
                <p className="text-muted-foreground" style={{fontSize:'clamp(0.8rem, 0.5vw + 0.6rem, 0.95rem)'}}>{t('cta.hotels')} • <TooltipProvider><Tooltip><TooltipTrigger asChild><span className="inline-flex items-center gap-1 cursor-help">{t('cta.bestPrice')} <Info className="w-3 h-3 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="bottom" className="max-w-xs text-sm"><p>{t('cta.bestPriceInfo', 'We leave out commission — hotels save 30% compared to other platforms, and we pass those savings directly to you.')}</p></TooltipContent></Tooltip></TooltipProvider></p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-foreground mb-2 block">{t('search.destination')}</Label>
                  {selectedHotel ? (
                    /* Selected Hotel Card */
                    <div className="relative p-3 rounded-xl bg-primary/10 border-2 border-primary/30">
                      <div className="flex items-center gap-3">
                        {selectedHotel.thumbnail && (
                          <img 
                            src={selectedHotel.thumbnail} 
                            alt={selectedHotel.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{selectedHotel.name}</p>
                          <p className="text-xs text-primary flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {t('search.hotelSelected', 'Hotel selected - pick your dates')}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          onClick={clearSelectedHotel}
                        >
                          <XCircle className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <DestinationAutocomplete 
                      value={destination}
                      onChange={setDestination}
                      onSelect={handleDestinationSelect}
                    />
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-foreground mb-2 block">{t('search.dates', 'Dates')}</Label>
                  <DateRangeSheet
                    checkIn={checkIn}
                    checkOut={checkOut}
                    onChange={({ from, to }) => {
                      setCheckIn(from);
                      setCheckOut(to);
                    }}
                    trigger={
                      <Button
                        variant="outline"
                        className="w-full h-14 rounded-xl bg-secondary/50 border-border justify-start text-left font-normal hover:bg-secondary transition-all"
                        data-testid="search-daterange"
                      >
                        <CalendarIcon className="mr-2 h-5 w-5 text-muted-foreground" />
                        {checkIn && checkOut
                          ? `${format(checkIn, 'MMM d')} — ${format(checkOut, 'MMM d')}`
                          : t('search.selectDates', 'Select dates')}
                      </Button>
                    }
                  />
                </div>

                <div className="flex items-center gap-3">
                  <GuestSelector 
                    guests={guests}
                    setGuests={setGuests}
                    childrenAges={childrenAges}
                    setChildrenAges={setChildrenAges}
                  />
                  {/* Country/Nationality Selector */}
                  <div className="flex items-center gap-2 bg-[#0a2540] rounded-xl px-3 py-2">
                    <CountrySelector compact />
                  </div>
                </div>

                <Button 
                  onClick={handleSearch} 
                  size="lg" 
                  className="w-full rounded-xl h-14 text-lg font-bold bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white shadow-[0_4px_0_#CC6200,0_5px_10px_rgba(0,0,0,0.2)] hover:brightness-110 active:shadow-[0_0px_0_#CC6200] active:translate-y-[4px] transition-all border-0"
                  data-testid="search-btn"
                >
                  {selectedHotel ? (
                    <>
                      <Building2 className="mr-2 w-5 h-5" />
                      {t('search.viewHotel', 'View Hotel')}
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 w-5 h-5" />
                      {t('search.searchButton')}
                    </>
                  )}
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    <span>{t('cta.securePayment')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    <TooltipProvider><Tooltip><TooltipTrigger asChild><span className="inline-flex items-center gap-1 cursor-help">{t('cta.bestPrice')} <Info className="w-3 h-3 text-muted-foreground" /></span></TooltipTrigger><TooltipContent side="bottom" className="max-w-xs text-sm"><p>{t('cta.bestPriceInfo', 'We leave out commission — hotels save 30% compared to other platforms, and we pass those savings directly to you.')}</p></TooltipContent></Tooltip></TooltipProvider>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS BRIDGE ========== */}
      <section 
        className="relative z-10 py-20 md:py-28 border-b border-gray-100 dark:border-white/10" 
        data-testid="how-it-works-bridge"
        style={{ backgroundColor: homepageBg.background_type === 'color' ? (homepageBg.solid_color || '#081D33') : undefined }}
      >
        <div className="max-w-[1760px] mx-auto px-4 md:px-8">
          <FadeInSection className="text-center mb-16">
            <Badge className="mb-3 bg-[#00A9E0]/10 text-[#00A9E0] border-[#00A9E0]/30">{t('howItWorks.badge', 'The Freestays Concept')}</Badge>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-3" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
              <span className="block text-white">{t('howItWorks.smartTitle1', 'See How It Works')}</span>
              <span className="block" style={{color: '#E8A317'}}>{t('howItWorks.smartTitle2', 'Save on Every Stay')}</span>
            </h2>
          </FadeInSection>
          <FadeInSection className="grid md:grid-cols-3 gap-8" delay={120}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-[0_3px_0_#CC6200]">1</div>
              <h3 className="font-bold text-lg mb-2">{t('howItWorks.bridge.step1', 'Search your stay')}</h3>
              <p className="text-muted-foreground text-sm">{t('howItWorks.bridge.step1Desc', 'Browse 450,000+ hotels worldwide with real-time availability and pricing.')}</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-[0_3px_0_#CC6200]">2</div>
              <h3 className="font-bold text-lg mb-2">{t('howItWorks.bridge.step2', 'Choose Pass or Voucher')}</h3>
              <p className="text-muted-foreground text-sm">{t('howItWorks.bridge.step2Desc', 'One-time voucher for €35 or unlimited bookings with the Annual Pass for €129/year.')}</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-[0_3px_0_#CC6200]">3</div>
              <h3 className="font-bold text-lg mb-2">{t('howItWorks.bridge.step3', 'Unlock the Freestays advantage')}</h3>
              <p className="text-muted-foreground text-sm">{t('howItWorks.bridge.step3Desc', 'Your room is included free with meal packages. Hotels save on commission — you save per stay.')}</p>
            </div>
          </FadeInSection>
          <p className="text-center text-muted-foreground text-sm mt-8 max-w-2xl mx-auto">
            {t('howItWorks.mechanicSupport', 'No discount codes. No hidden steps. On qualifying bookings, the room price is removed when your pass is applied.')}
          </p>

          {/* Price Comparison Examples from Admin */}
          {homepageComparisons.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-extrabold leading-tight tracking-tight mb-2" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
                  <span className="block text-white">{t('howItWorks.exampleTitle', 'What this looks like on a booking')}</span>
                </h3>
                <p className="text-muted-foreground text-sm">{t('howItWorks.exampleSubtitle', 'Based on actual hotel prices from our network')}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {homepageComparisons.map((comp, idx) => (
                  <Card key={comp.comparison_id || idx} className="rounded-2xl overflow-hidden border-border" data-testid={`homepage-comparison-${idx}`}>
                    {comp.hotel_image_url && (
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={proxyImage(comp.hotel_image_url, 662, 80)}
                          alt={comp.hotel_name || 'Hotel'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                          width="662"
                          height="160"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h4 className="font-semibold text-lg mb-1">{comp.hotel_name || comp.destination || "Hotel"}</h4>
                      <p className="text-muted-foreground text-xs mb-1">{comp.destination}</p>
                      <p className="text-muted-foreground text-xs mb-4">
                        {comp.check_in} - {comp.check_out} | {comp.nights || 6} {t('search.nights', 'nights')} | {comp.adults || 2} {t('search.adults', 'adults')}
                      </p>
                      {(() => {
                        const isLoggedIn = !!user;
                        const computedSavings = (comp.hotels || []).reduce((sum, h) => {
                          const other = h.ota_estimated_price || 0;
                          const fsBase = h.freestays_price || h.price || 0;
                          const fsFinal = isLoggedIn ? fsBase * 0.85 : fsBase;
                          return sum + Math.max(0, other - fsFinal);
                        }, 0);
                        return (
                          <>
                            {comp.hotels?.map((hotel, hi) => {
                              const otherPrice = hotel.ota_estimated_price || 0;
                              const fsBase = hotel.freestays_price || hotel.price || 0;
                              const fsFinal = isLoggedIn ? fsBase * 0.85 : fsBase;
                              return (
                                <div key={hi} className="space-y-2 mb-3" data-testid={`homepage-comp-row-${idx}-${hi}`}>
                                  <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{hotel.board_type}</div>
                                  {otherPrice > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-muted-foreground">{t('comparisonPage.otherSites', 'Other sites')}</span>
                                      <span className={`text-sm ${isLoggedIn ? 'line-through text-red-400' : 'font-bold text-base'}`}>&euro;{otherPrice.toFixed(0)}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-[#E8A317] inline-flex items-center gap-1">
                                      {t('comparisonPage.freestaysPrice', 'Freestays Price')} ★
                                      {isLoggedIn && (
                                        <TooltipProvider delayDuration={150}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Info className="w-3 h-3 text-[#E8A317]/70 cursor-help" data-testid={`homepage-comp-discount-info-${idx}-${hi}`} />
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs text-xs">
                                              <p>{t('comparisonPage.discountAutoApplied', 'Korting wordt automatisch toegepast bij checkout')}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </span>
                                    <span className="text-base font-bold text-[#E8A317]">&euro;{fsFinal.toFixed(2).replace('.', ',')}</span>
                                  </div>
                                  {!isLoggedIn && (
                                    <p className="text-xs text-[#00A9E0] italic">{t('comparisonPage.loginDiscount', 'Login to check your discount')}</p>
                                  )}
                                </div>
                              );
                            })}
                            {computedSavings > 0 && (
                              <div className="mt-3 pt-3 border-t border-border rounded-lg bg-green-50 dark:bg-green-950/20 p-3" data-testid={`homepage-comp-savings-${idx}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm flex items-center gap-1 text-muted-foreground">
                                    <TrendingDown className="w-4 h-4 text-green-600" /> {t('comparisonPage.youSave', 'You save')}
                                  </span>
                                  <span className="font-bold text-green-600">&euro;{computedSavings.toFixed(0)}</span>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/comparison">
              <Button variant="outline" className="rounded-xl px-6 border-[#00A9E0] text-[#00A9E0] hover:bg-[#00A9E0]/10 font-semibold" data-testid="bridge-cta-comparison">
                {t('comparisonPage.title', 'The Freestays Advantage')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Hotels (admin-curated TOP SELLER list) + Trending Destinations carousel */}
      <FeaturedHotelsCarousel listType="top_booked" limit={12} />
      <PopularDestinationsCarousel limit={8} />

      {/* ========== VIDEO BACKGROUND SECTION START ========== */}
      <VideoBackground
        videoSrcWebm={homepageBg.video_url}
        videoSrcMp4={homepageBg.video_url_mp4}
        posterSrc={homepageBg.poster_url}
        imageUrl={homepageBg.image_url}
        overlayOpacity={homepageBg.overlay_opacity / 100}
        textColor={homepageBg.text_color}
        backgroundType={homepageBg.background_type || 'video'}
        gradientStyle={homepageBg.gradient_style || 'ocean'}
        solidColor={homepageBg.solid_color || '#081D33'}
        enabled={homepageBg.enabled !== false}
        className="relative"
      >
        {/* Last Minute Deals */}
        <div className="relative z-10">
          <LastMinuteSection />
        </div>

        {/* Value Proposition - Freestays Pass */}
        <section className="relative z-10 py-20 md:py-28">
          <div className="max-w-[1760px] mx-auto px-4 md:px-8">
            <FadeInSection className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-4" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
                <span className="block text-white">{t('pass.smartTitle1', 'Unlock Your Pass')}</span>
                <span className="block" style={{color: '#E8A317'}}>{t('pass.smartTitle2', 'Travel for Less')}</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {t('pass.sectionSubtitle', 'Your room is included with a meal package. Choose the Freestays option that fits your plans.')}
              </p>
            </FadeInSection>

            <FadeInSection className="grid md:grid-cols-2 gap-8 max-w-[1760px] mx-auto" delay={120}>
              {/* One-Time Pass */}
              <Card className="rounded-3xl bg-white dark:bg-[#0a2540] border-2 border-gray-200 dark:border-white/10 shadow-xl card-hover relative overflow-hidden">
                <img 
                  src="/api/media/image/logo/voucher_home.webp?w=1280&q=82"
                  alt="Freestays One-Time Pass" 
                  className="w-full h-auto object-cover rounded-t-3xl"
                  width="1280"
                  height="715"
                  loading="lazy"
                  decoding="async"
                />
                <div className="p-6">
                <h3 className="font-bold text-2xl mb-2">{t('pass.oneTimePass', 'One-Time Access')}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-[#00A9E0]">€35</span>
                  <span className="text-muted-foreground">{t('pass.perBooking', 'per booking')}</span>
                </div>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t('pass.oneTimeDesc', 'Best for one planned trip. Clear, simple, and easy to use from the start.')}
                </p>
                <div className="space-y-2 text-sm mb-6">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    <span>{t('pass.roomFree', 'Your room is covered')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    <span>{t('pass.onlyPayMeals', 'You only pay for meals')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    <span>{t('pass.noBookingFee', 'No extra booking fees')}</span>
                  </div>
                </div>
                {user ? (
                  <Button className="w-full rounded-xl" variant="outline" onClick={() => navigate('/dashboard?tab=passcode')} data-testid="homepage-get-onetime-pass">{t('pass.getPass', 'Choose One-Time Access')}</Button>
                ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full rounded-xl" variant="outline" data-testid="homepage-get-onetime-pass">{t('pass.getPass', 'Choose One-Time Access')}</Button>
                  </DialogTrigger>
                  <AuthDialog defaultTab="register" />
                </Dialog>
                )}
                <Link to="/voucher" className="block mt-3">
                  <Button variant="link" className="w-full text-[#00A9E0] text-sm p-0 h-auto" data-testid="homepage-learn-voucher">
                    {t('voucherPage.title', 'Learn more about Vouchers')} <ArrowRight className="ml-1 w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Annual Pass - Featured */}
            <Card className="rounded-3xl bg-[#081D33] border-2 border-[#FFAF00]/30 shadow-2xl card-hover relative overflow-hidden">
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-[#E8A317] text-[#081D33] font-bold">{t('pass.bestValue', 'Best value for frequent travelers')}</Badge>
              </div>
              <img 
                src="/api/media/image/logo/annual_pass_home.webp?w=1280&q=82"
                alt="Freestays Annual Pass" 
                className="w-full h-auto object-cover rounded-t-3xl"
                width="1280"
                height="715"
                loading="lazy"
                decoding="async"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-1 text-white">{t('pass.annualPass', 'Annual Pass')}</h3>
                <p className="text-white/60 text-sm mb-3">{t('pass.annualSubtitle', 'Smarter booking all year round')}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-[#E8A317]">€129</span>
                  <span className="text-white/70">{t('pass.perYear', '/ year')}</span>
                </div>
                <p className="text-white/80 mb-4 text-sm">
                  {t('pass.annualDesc', 'Best for travelers who book more than once a year. One setup, year-round access.')}
                </p>
                <div className="space-y-2 text-sm mb-4 text-white">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>{t('pass.annualBullet1', 'Best for travelers who book more than once a year')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>{t('pass.annualBullet2', 'Your room is included with meal package bookings')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>{t('pass.annualBullet3', 'No booking fees on Freestays reservations')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>{t('pass.annualBullet4', 'Priority support throughout the year')}</span>
                  </div>
                </div>
                <p className="text-[#E8A317] font-semibold text-sm mb-4 italic">{t('pass.annualHighlight', 'One pass. Better booking value all year.')}</p>
                {user ? (
                  <Button className="w-full rounded-xl bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white hover:brightness-110 font-bold shadow-[0_4px_0_#CC6200] border-0" onClick={() => navigate('/dashboard?tab=passcode')} data-testid="homepage-get-annual-pass">
                      {t('pass.getAnnualPass', 'Choose Annual Pass')}
                    </Button>
                ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full rounded-xl bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white hover:brightness-110 font-bold shadow-[0_4px_0_#CC6200] border-0" data-testid="homepage-get-annual-pass">
                      {t('pass.getAnnualPass', 'Choose Annual Pass')}
                    </Button>
                  </DialogTrigger>
                  <AuthDialog defaultTab="register" />
                </Dialog>
                )}
                <p className="text-white/50 text-xs text-center mt-3">{t('pass.annualBottomLine', 'Book year-round with a simpler and fairer way to stay.')}</p>
                <Link to="/pass" className="block mt-2">
                  <Button variant="link" className="w-full text-white/60 text-sm p-0 h-auto hover:text-[#E8A317]" data-testid="homepage-learn-pass">
                    {t('passPage.title', 'Learn more about the Pass')} <ArrowRight className="ml-1 w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </Card>
          </FadeInSection>
          
          {/* Guest Booking Info */}
          <FadeInSection className="mt-8 text-center" delay={200}>
            <p className="text-muted-foreground text-sm">
              {t('pass.noPassOption', 'No pass? No problem! Book as a guest with no booking fee.')}
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="relative z-10 py-20 md:py-28">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div>
              <Badge className="mb-3 bg-[#00A9E0]/10 text-[#00A9E0] border-[#00A9E0]/30">{t('destinations.badge', 'Popular Destinations')}</Badge>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-2" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
                <span className="block text-white">{t('destinations.smartTitle1', 'Where Will You Go?')}</span>
                <span className="block" style={{color: '#E8A317'}}>{t('destinations.smartTitle2', 'Discover Dream Stays')}</span>
              </h2>
              <p className="text-gray-700 dark:text-gray-700">{t('destinations.subtitle', 'Discover stunning destinations with commission-free stays')}</p>
            </div>
            <Link 
              to={userCountryDestination 
                ? `/search?destination=${encodeURIComponent(userCountryDestination.name)}&destinationId=${userCountryDestination.destinationId}&checkIn=${format(addDays(new Date(), 1), 'yyyy-MM-dd')}&checkOut=${format(addDays(new Date(), 4), 'yyyy-MM-dd')}&adults=2&children=0&rooms=1`
                : "/search"
              } 
              className="inline-flex items-center text-sm font-medium text-primary hover:underline mt-3"
              data-testid="destinations-view-all"
            >
              {t('destinations.viewAll', 'Bekijk alles')} <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featuredDestinations.slice(0, destinationsCount).map((dest, idx) => {
              // Default dates: tomorrow to 3 days later
              const defaultCheckIn = format(addDays(new Date(), 1), 'yyyy-MM-dd');
              const defaultCheckOut = format(addDays(new Date(), 4), 'yyyy-MM-dd');
              // Make first 2 items larger on desktop
              const isLarge = idx < 2;
              return (
              <Link 
                key={dest.name} 
                to={`/search?destination=${encodeURIComponent(dest.name)}&destinationId=${dest.destination_id}&checkIn=${defaultCheckIn}&checkOut=${defaultCheckOut}&adults=2&children=0&rooms=1`}
                className={`group relative rounded-2xl md:rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-1 ${isLarge ? 'md:col-span-2 aspect-[16/10]' : 'aspect-[4/5]'}`}
                data-testid={`destination-${dest.name.toLowerCase()}`}
              >
                <img 
                  src={dest.image} 
                  alt={dest.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute top-3 right-3 md:top-4 md:right-4">
                  <Badge className="bg-white/20 backdrop-blur-md text-white border-white/20 text-xs px-3 py-1">
                    {dest.hotels} {t('destinations.hotels', 'hotels')}
                  </Badge>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <h3 className={`font-sans font-bold text-white mb-1 ${isLarge ? 'text-2xl md:text-3xl' : 'text-lg md:text-2xl'}`}>{dest.name}</h3>
                  <p className="text-white/80 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {dest.country}
                  </p>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
              </Link>
            );
            })}
          </div>
        </div>
      </section>

      {/* ========== BENEFITS / COMPARISON BRIDGE ========== */}
      <section className="relative z-10 py-20 md:py-28" data-testid="benefits-comparison-bridge">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8">
          <FadeInSection className="text-center mb-12">
            <Badge className="mb-3 bg-primary/10 dark:bg-white/20 backdrop-blur text-primary dark:text-white border-primary/30 dark:border-white/30">{t('comparison.title', 'See The Difference')}</Badge>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-3" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
              <span className="block text-white">{t('comparison.smartTitle1', 'See The Difference')}</span>
              <span className="block" style={{color: '#E8A317'}}>{t('comparison.smartTitle2', 'Compare & Save')}</span>
            </h2>
            <p className="text-gray-700 dark:text-gray-700 max-w-2xl mx-auto">{t('comparisonPage.howDesc')}</p>
          </FadeInSection>

          <FadeInSection className="grid md:grid-cols-3 gap-8 max-w-[1760px] mx-auto" delay={120}>
            {/* Traditional */}
            <Card className="rounded-2xl overflow-hidden border-white/[0.06] bg-[#0a2540]/60" data-testid="homepage-comparison-card-traditional">
              <div className="w-full h-40 overflow-hidden grayscale opacity-60">
                <img src="/api/media/image/all_in_one_picture.webp" alt="Traditional booking" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-lg text-white mb-1">{t('comparisonPage.traditional', 'Traditional booking')}</h3>
                <p className="text-xs text-white/40 mb-5">{t('comparisonPage.traditionalSubtitle', 'More cost. More doubt.')}</p>
                <div className="space-y-3">
                  {[t('comparisonPage.trad1', 'You pay for everything'), t('comparisonPage.trad2', "Prices aren't always transparent"), t('comparisonPage.trad3', 'Easy to overpay'), t('comparisonPage.trad4', 'You have to figure it out yourself')].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-white/50">
                      <X className="w-4 h-4 flex-shrink-0 text-red-400/50" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* One-time / Voucher */}
            <Card className="rounded-2xl overflow-hidden border-white/10 bg-[#0a2540]" data-testid="homepage-comparison-card-voucher">
              <div className="w-full h-40 overflow-hidden">
                <img src="/api/media/image/logo/voucher_partner.webp" alt="Freestays Voucher" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-lg text-white mb-1">{t('comparisonPage.withVoucher', 'Freestays — One-time')}</h3>
                <p className="text-xs text-white/50 mb-5">{t('comparisonPage.voucherSubtitle', 'One clear booking')}</p>
                <div className="space-y-3">
                  {[t('comparisonPage.voucher1', 'Room included'), t('comparisonPage.voucher2', 'Pay only for meals'), t('comparisonPage.voucher3', 'No surprises')].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-white">
                      <Check className="w-4 h-4 flex-shrink-0 text-[#E8A317]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#E8A317]/70 mt-4 italic">{t('comparisonPage.voucherTagline', 'Everything is handled upfront')}</p>
              </div>
            </Card>

            {/* Annual Pass */}
            <Card className="rounded-2xl overflow-hidden border-[#E8A317]/30 bg-[#0a2540] relative" data-testid="homepage-comparison-card-annual">
              <Badge className="absolute top-4 right-4 bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white text-[10px] px-2 py-0.5 leading-tight font-bold rounded-full z-10">{t('comparisonPage.bestValueLabel', 'Best value')}</Badge>
              <div className="w-full h-40 overflow-hidden">
                <img src="/api/media/image/logo/smart_savers.webp" alt="Freestays Annual Pass" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-lg text-white mb-1">{t('comparisonPage.withPass', 'Freestays — Annual')}</h3>
                <p className="text-xs text-white/50 mb-5">{t('comparisonPage.passSubtitle', 'One decision for the whole year')}</p>
                <div className="space-y-3">
                  {[t('comparisonPage.annual1', 'Room included on every stay'), t('comparisonPage.annual2', 'Pay only for meals'), t('comparisonPage.annual3', 'No need to rethink')].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-white">
                      <Check className="w-4 h-4 flex-shrink-0 text-[#E8A317]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#E8A317]/70 mt-4 italic">{t('comparisonPage.passTagline', 'Just book and move on')}</p>
              </div>
            </Card>
          </FadeInSection>

          {/* Feature comparison matrix (mirrored from /comparison) */}
          <FadeInSection className="mt-14 max-w-[1760px] mx-auto" delay={150} data-testid="homepage-comparison-matrix">
            <h3 className="font-semibold text-xl text-white text-center mb-6">
              {t('comparisonPage.matrixTitle', 'Side-by-side comparison')}
            </h3>
            <div className="overflow-x-auto rounded-2xl border border-white/[0.06] bg-[#0a2540]/60">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="bg-[#081d33]">
                    <th className="text-left text-white/60 font-medium px-2 sm:px-4 py-3 border-b border-white/10">{t('comparisonPage.matrixFeature', 'Feature')}</th>
                    <th className="text-center text-white/60 font-medium px-2 sm:px-4 py-3 border-b border-white/10">
                      <span className="sm:hidden">{t('comparisonPage.traditionalShort', 'Traditional')}</span>
                      <span className="hidden sm:inline">{t('comparisonPage.traditional', 'Traditional booking')}</span>
                    </th>
                    <th className="text-center text-white font-medium px-2 sm:px-4 py-3 border-b border-white/10">
                      <span className="sm:hidden">{t('comparisonPage.withVoucherShort', 'One-time')}</span>
                      <span className="hidden sm:inline">{t('comparisonPage.withVoucher', 'Freestays — One-time')}</span>
                    </th>
                    <th className="text-center text-[#E8A317] font-semibold px-2 sm:px-4 py-3 border-b border-white/10">
                      <span className="sm:hidden">{t('comparisonPage.withPassShort', 'Annual')}</span>
                      <span className="hidden sm:inline">{t('comparisonPage.withPass', 'Freestays — Annual')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: t('comparisonPage.matrixRow1', 'Room included with meal package'), cols: [false, true, true] },
                    { label: t('comparisonPage.matrixRow2', 'Pay only for meals'), cols: [false, true, true] },
                    { label: t('comparisonPage.matrixRow3', 'Transparent pricing upfront'), cols: [false, true, true] },
                    { label: t('comparisonPage.matrixRow4', 'No booking fees'), cols: [false, true, true] },
                    { label: t('comparisonPage.matrixRow5', 'Unlimited bookings per year'), cols: [false, false, true] },
                    { label: t('comparisonPage.matrixRow6', 'Priority support'), cols: [false, false, true] },
                  ].map((row, ri) => (
                    <tr key={ri} className="border-b border-white/5 last:border-b-0">
                      <td className="text-left text-white/80 px-2 sm:px-4 py-3">{row.label}</td>
                      {row.cols.map((ok, ci) => (
                        <td key={ci} className="text-center px-2 sm:px-4 py-3">
                          {ok ? (
                            <Check className={`w-4 h-4 sm:w-5 sm:h-5 inline-block ${ci === 2 ? 'text-[#E8A317]' : 'text-green-400'}`} />
                          ) : (
                            <X className="w-4 h-4 sm:w-5 sm:h-5 inline-block text-red-400/50" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-[#081d33]/60">
                    <td className="text-left text-white/80 font-semibold px-2 sm:px-4 py-3">{t('comparisonPage.matrixPrice', 'Price')}</td>
                    <td className="text-center text-white/50 px-2 sm:px-4 py-3">{t('comparisonPage.matrixPriceTrad', 'Variable')}</td>
                    <td className="text-center text-white font-semibold px-2 sm:px-4 py-3">€35</td>
                    <td className="text-center text-[#E8A317] font-bold px-2 sm:px-4 py-3 whitespace-nowrap">€129{t('comparisonPage.matrixPerYear', '/yr')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </FadeInSection>

          {/* FAQ Block — 7 groups, 30 items */}
          <FadeInSection className="max-w-4xl mx-auto mt-12" delay={150} data-testid="homepage-faq-block">
            <div className="text-center mb-8">
              <p className="text-xs uppercase tracking-widest text-[#E8A317] font-semibold mb-2">FAQ</p>
              <h3 className="font-bold text-xl text-white">{t('comparison.faqTitle', 'Common Questions')}</h3>
              <p className="text-white/50 text-sm mt-1">{t('comparison.faqSubtitle', 'Everything you need to know about the Freestays Pass.')}</p>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {[1,2,3,4,5,6,7].map((g) => (
                <AccordionItem key={g} value={`faq-group-${g}`} className="border border-white/10 rounded-xl bg-white/5 overflow-hidden" data-testid={`faq-group-${g}`}>
                  <AccordionTrigger className="text-white font-semibold hover:no-underline px-5 py-4 text-sm" data-testid={`faq-group-trigger-${g}`}>
                    {t(`comparison.faqG${g}Title`)}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4">
                    <Accordion type="single" collapsible className="space-y-1.5">
                      {Array.from({ length: g === 2 ? 3 : g === 5 || g === 6 || g === 7 ? 4 : 5 }, (_, i) => i + 1).map((q) => (
                        <AccordionItem key={q} value={`faq-g${g}-q${q}`} className="border border-white/[0.06] rounded-lg bg-white/[0.03] overflow-hidden">
                          <AccordionTrigger className="text-white/80 text-sm hover:no-underline px-4 py-2.5 font-normal" data-testid={`faq-g${g}-q${q}-trigger`}>
                            {t(`comparison.faqG${g}Q${q}`)}
                          </AccordionTrigger>
                          <AccordionContent className="text-white/60 text-sm px-4">
                            {t(`comparison.faqG${g}A${q}`)}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeInSection>

          <FadeInSection className="text-center mt-10" delay={200}>
            <Link to="/comparison">
              <Button className="rounded-full px-8 bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100" data-testid="benefits-cta-comparison">
                {t('comparisonPage.title', 'The Freestays Advantage')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </FadeInSection>
        </div>
      </section>

      </VideoBackground>
      {/* ========== VIDEO BACKGROUND SECTION END ========== */}

      {/* Limited Time Pass Offer */}
      <GradientBackground
        gradientSrc="/videos/ocean-gradient.jpg"
        overlayOpacity={0.7}
        solidColor={homepageBg.background_type === 'color' ? (homepageBg.solid_color || '#081D33') : null}
        className="relative z-10 py-20 md:py-28 overflow-hidden"
      >
        
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 text-center relative">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Gift className="w-4 h-4" />
            {t('pass.limitedTime', 'Limited Time: Get Your First Room FREE')}
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
            <span className="block text-white">{t('cta.smartTitle1', 'Ready for Free?')}</span>
            <span className="block" style={{color: '#E8A317'}}>{t('cta.smartTitle2', 'Start Your Journey')}</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            {t('cta.heroSubtitle', 'Join thousands of smart travelers who get their rooms for FREE. Start with €35 one-time pass or go unlimited with €129/year.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button size="lg" className="rounded-full px-10 h-14 text-lg bg-primary hover:bg-primary/90 text-white font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all" data-testid="cta-signup">
                  <Search className="mr-2 w-5 h-5" />
                  {t('hero.secondaryCta', 'Search Hotels Now')}
                </Button>
              </DialogTrigger>
              <AuthDialog defaultTab="register" />
            </Dialog>
            {user ? (
              <Link to="/pass">
                <Button size="lg" variant="outline" className="rounded-full px-10 h-14 text-lg border-2 border-primary/30 text-primary hover:bg-[#00A9E0]/5" data-testid="mid-cta-pass">
                  <Sparkles className="mr-2 w-5 h-5" />
                  {t('passPage.title', 'Freestays Pass')}
                </Button>
              </Link>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="rounded-full px-10 h-14 text-lg border-2 border-primary/30 text-primary hover:bg-[#00A9E0]/5" data-testid="mid-cta-pass">
                    <Sparkles className="mr-2 w-5 h-5" />
                    {t('passPage.title', 'Freestays Pass')}
                  </Button>
                </DialogTrigger>
                <AuthDialog defaultTab="register" />
              </Dialog>
            )}
            {user ? (
              <Link to="/voucher">
                <Button size="lg" variant="ghost" className="rounded-full px-8 h-14 text-lg text-primary hover:bg-[#00A9E0]/5" data-testid="mid-cta-voucher">
                  {t('voucherPage.title', 'Freestays Voucher')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" variant="ghost" className="rounded-full px-8 h-14 text-lg text-primary hover:bg-[#00A9E0]/5" data-testid="mid-cta-voucher">
                    {t('voucherPage.title', 'Freestays Voucher')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <AuthDialog defaultTab="register" />
              </Dialog>
            )}
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-12 pt-8 border-t border-border flex flex-wrap justify-center gap-8 text-muted-foreground text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span>{t('cta.securePayment', 'Secure Payment')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <span>{t('cta.hotels', '450,000+ Hotels')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              <TooltipProvider><Tooltip><TooltipTrigger asChild><span className="inline-flex items-center gap-1 cursor-help">{t('cta.bestPrice', 'Best Price Guarantee')} <Info className="w-3.5 h-3.5" /></span></TooltipTrigger><TooltipContent side="bottom" className="max-w-xs text-sm"><p>{t('cta.bestPriceInfo', 'We leave out commission — hotels save 30% compared to other platforms, and we pass those savings directly to you.')}</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </div>
        </div>
      </GradientBackground>

      {/* Write Review Section - MOVED UP (was block 9, now block 8) */}
      <TestimonialSubmitForm 
        isOpen={showReviewForm} 
        onClose={() => setShowReviewForm(false)} 
      />
      
      {/* Testimonials Section - MOVED DOWN (was block 8, now block 9) */}
      <div 
        className="relative z-10" 
        style={homepageBg.background_type === 'color' ? { backgroundColor: homepageBg.solid_color || '#081D33' } : undefined}
      >
        <div style={homepageBg.background_type === 'color' ? { ['--testimonials-bg']: 'transparent' } : undefined}>
          <TestimonialsSection bgOverride={homepageBg.background_type === 'color' ? 'transparent' : null} />
        </div>
        <div className="text-center pb-12 -mt-4">
          <Link to="/comparison">
            <Button variant="link" className="text-primary" data-testid="testimonials-cta-comparison">
              {t('comparisonPage.title', 'See how it works')} <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Popular Destinations - SEO Internal Linking */}
      <div style={homepageBg.background_type === 'color' ? { backgroundColor: homepageBg.solid_color || '#081D33' } : undefined}>
        <PopularDestinationsGrid limit={8} />
      </div>

      {/* Hotel Brand Partners + Manifesto Banner - COMBINED IN NAVY THEME (blocks 9+10) */}
      <section className="relative z-10 bg-[#081D33] overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00A9E0]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E8A317]/10 rounded-full blur-3xl" />
        </div>
        
        {/* Hotel Brand Partners - Inside Blue Banner */}
        <div className="py-12 relative z-10 border-b border-white/10">
          <div className="max-w-[1760px] mx-auto px-4 md:px-8 text-center">
            <p className="mb-8 text-white/80 text-sm font-medium">{t('brands.title', 'Trusted by guests at world-class hotel brands')}</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              <img src="/assets/brands/marriott.webp" alt="Marriott" width="96" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/sheraton.webp" alt="Sheraton" width="115" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/holidayinn.webp" alt="Holiday Inn" width="136" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/conrad.webp" alt="Conrad" width="373" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/radisson.webp" alt="Radisson" width="96" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/hyatt.webp" alt="Hyatt" width="182" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/ritz.webp" alt="Ritz-Carlton" width="137" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
              <img src="/assets/brands/hilton.webp" alt="Hilton" width="143" height="96" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity brightness-0 invert" />
            </div>
          </div>
        </div>
        
        {/* Manifesto Content */}
        <div className="py-16 md:py-24 relative z-10">
          <div className="max-w-[1760px] mx-auto px-4 md:px-8 text-center">
            {/* Intro */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight mb-10" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
              <span className="block text-white">{t('manifesto.headline1', 'Not a deal.')}</span>
              <span className="block" style={{color: '#E8A317'}}>{t('manifesto.headline2', 'A different standard.')}</span>
            </h2>

            {/* Audience */}
            <div className="mb-10">
              <p className="text-lg md:text-xl text-white/80 font-medium mb-6">
                {t('manifesto.freestaysIsFor', 'Freestays is for those who:')}
              </p>
              <div className="space-y-3 max-w-xl mx-auto">
                <p className="text-white/90 text-base md:text-lg flex items-center gap-3 justify-center">
                  <span className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  {t('manifesto.audience1', "don't want to overcompare")}
                </p>
                <p className="text-white/90 text-base md:text-lg flex items-center gap-3 justify-center">
                  <span className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  {t('manifesto.audience2', "don't trust unclear pricing")}
                </p>
                <p className="text-white/90 text-base md:text-lg flex items-center gap-3 justify-center">
                  <span className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  {t('manifesto.audience3', "don't need more options — just the right one")}
                </p>
                <p className="text-white/90 text-base md:text-lg flex items-center gap-3 justify-center">
                  <span className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                  {t('manifesto.audience4', 'expect things to work, instantly')}
                </p>
              </div>
            </div>

            {/* Identity */}
            <div className="mb-8">
              <p className="text-lg md:text-xl text-white/70 italic max-w-lg mx-auto">
                {t('manifesto.identity', 'For a generation that moves faster than outdated booking systems.')}
              </p>
            </div>

            {/* Trigger */}
            <div className="mb-8">
              <p className="text-xl md:text-2xl text-white font-semibold max-w-lg mx-auto">
                {t('manifesto.trigger', 'Once you experience it, booking the old way feels unnecessary.')}
              </p>
            </div>

            {/* Closing */}
            <div className="mb-10 space-y-2">
              <p className="text-lg md:text-xl text-white/80">
                {t('manifesto.closing', 'Stop adjusting to the system. Use one that makes sense.')}
              </p>
              <p className="text-2xl md:text-3xl text-white font-bold mt-4">
                {t('manifesto.welcome', 'Freestays.')}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/search">
                <Button 
                  size="lg" 
                  className="rounded-xl px-8 h-14 text-lg bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white hover:brightness-110 font-bold shadow-[0_4px_0_#CC6200,0_5px_10px_rgba(0,0,0,0.3)] border-0 transition-all"
                  data-testid="manifesto-search-btn"
                >
                  <Search className="mr-2 w-5 h-5" />
                  {t('manifesto.searchHotels', 'Search Hotels')}
                </Button>
              </Link>
              
              <Link to="/pass">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-xl px-8 h-14 text-lg bg-transparent border-2 border-white/40 text-white hover:bg-white/10 font-bold"
                  data-testid="manifesto-pass-btn"
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  {t('passPage.title', 'Freestays Pass')}
                </Button>
              </Link>
              
              <Link to="/comparison">
                <Button 
                  size="lg" 
                  variant="ghost"
                  className="rounded-full px-8 h-14 text-lg text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="manifesto-comparison-btn"
                >
                  {t('comparisonPage.title', 'The Freestays Advantage')} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
